import { Button, ButtonLink } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Page, PageHeader } from '@/components/layout/page';
import { Input } from '@/components/ui/field';

import {
    Alert,
    Card,
    CardBody,
    Container,
    Section,
} from '@/components/ui/primitives';

import { dashlite } from '@/lib/dashlite';
import { fieldErrors, presentError } from '@/lib/errors';
import { paths } from '@/lib/paths';

/**
 * Désinscription de la newsletter — `POST /newsletter/unsubscribe`.
 *
 * Cette page couvre le cas d'une désinscription manuelle.
 * L'adresse peut être pré-remplie depuis la query string `?email=...`.
 */
export default function UnsubscribePage() {
    const [searchParams] = useSearchParams();

    const [email, setEmail] = useState(
        searchParams.get('email') ?? '',
    );

    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const fields = fieldErrors(error);

    useEffect(() => {
        const fromUrl = searchParams.get('email');

        if (fromUrl) {
            setEmail(fromUrl);
        }
    }, [searchParams]);

    async function handleSubmit(
        event: FormEvent<HTMLFormElement>,
    ): Promise<void> {
        event.preventDefault();

        setSubmitting(true);
        setError(null);

        try {
            await dashlite.unsubscribeNewsletter({
                email: email.trim(),
            });

            setDone(true);
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Page title="Se désinscrire">
            <PageHeader
                eyebrow=""
                title="Résilier l'abonnement"
                description="Processus d'annulation rapide, sécurisé et sans frais"
            />

            <Section>
                <Container className="max-w-xl space-y-6">
                    {done ? (
                        <>
                            <Alert
                                tone="positive"
                                title="C'est fait"
                            >
                                Cette adresse ne recevra plus aucun message :
                                ni campagne, ni séquence en cours. Si vous
                                changez d'avis, vous pourrez vous réinscrire à
                                tout moment.
                            </Alert>

                            <div className="flex flex-wrap gap-3">
                                <ButtonLink
                                    to={paths.home}
                                    variant="secondary"
                                >
                                    Retour à l'accueil
                                </ButtonLink>

                                <ButtonLink
                                    to={paths.newsletter}
                                    variant="ghost"
                                >
                                    Me réinscrire
                                </ButtonLink>
                            </div>
                        </>
                    ) : (
                        <Card>
                            <CardBody>
                                <form
                                    onSubmit={handleSubmit}
                                    className="space-y-5"
                                    noValidate
                                >
                                    <Input
                                        label="Adresse email"
                                        type="email"
                                        required
                                        autoFocus
                                        autoComplete="email"
                                        value={email}
                                        error={fields.email}
                                        onChange={(event) =>
                                            setEmail(event.target.value)
                                        }
                                    />

                                    {error !== null &&
                                        Object.keys(fields).length === 0 && (
                                            <Alert
                                                tone="critical"
                                                title={
                                                    presentError(error).message
                                                }
                                            >
                                                {presentError(error).hint}
                                            </Alert>
                                        )}

                                    <div className="flex gap-3 rounded-xl border border-zinc-100 bg-zinc-50 p-4">
                                        <p className="text-sm leading-relaxed text-zinc-600">
                                            Nous avons besoin de votre email
                                            pour retrouver votre dossier de
                                            facturation en toute sécurité.
                                        </p>
                                    </div>

                                    <Button
                                        type="submit"
                                        size="lg"
                                        loading={submitting}
                                        disabled={email.trim() === ''}
                                        className="w-full bg-[#b20303] text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#950202] hover:shadow-lg"
                                    >
                                        Me désinscrire
                                    </Button>
                                </form>
                            </CardBody>
                        </Card>
                    )}
                </Container>
            </Section>
        </Page>
    );
}